/**
 * useFileContext Hook
 *
 * Manages file attachments as context for AI queries.
 * Supports drag-and-drop file reading with size limits.
 * Supports .docx (via mammoth) and .pdf (via pdf.js) with dynamic imports.
 */

import { useState, useCallback } from 'react';
import { loadPdfJs } from '@fias/pdf-core/loader';

/** Maximum size per file in bytes (500KB for binary files like PDF/DOCX) */
export const MAX_FILE_SIZE = 500 * 1024;

/** Maximum total context size in bytes (500KB of extracted text) */
export const MAX_TOTAL_CONTEXT_SIZE = 500 * 1024;

/** Maximum number of files */
export const MAX_FILES = 10;

/** Supported text file extensions for context */
export const SUPPORTED_TEXT_EXTENSIONS = new Set([
  // Code files
  '.js',
  '.jsx',
  '.ts',
  '.tsx',
  '.py',
  '.java',
  '.c',
  '.cpp',
  '.h',
  '.hpp',
  '.cs',
  '.go',
  '.rs',
  '.rb',
  '.php',
  '.swift',
  '.kt',
  '.scala',
  '.r',
  // Web files
  '.html',
  '.htm',
  '.css',
  '.scss',
  '.sass',
  '.less',
  '.vue',
  '.svelte',
  // Data/config files
  '.json',
  '.xml',
  '.yaml',
  '.yml',
  '.toml',
  '.ini',
  '.env',
  '.properties',
  // Documentation
  '.txt',
  '.md',
  '.mdx',
  '.rst',
  '.tex',
  '.csv',
  '.tsv',
  // Shell/scripts
  '.sh',
  '.bash',
  '.zsh',
  '.fish',
  '.ps1',
  '.bat',
  '.cmd',
  // SQL
  '.sql',
  // Other
  '.log',
  '.conf',
  '.cfg',
  '.dockerfile',
  '.gitignore',
  '.editorconfig',
]);

/** Document file extensions that require special processing */
export const DOCUMENT_EXTENSIONS = new Set(['.docx', '.pdf']);

/** All supported extensions */
export const SUPPORTED_EXTENSIONS = new Set([...SUPPORTED_TEXT_EXTENSIONS, ...DOCUMENT_EXTENSIONS]);

/** File with its content for context */
export interface FileContext {
  /** Unique ID for the file */
  id: string;
  /** Original file name */
  name: string;
  /** File size in bytes */
  size: number;
  /** File content as text */
  content: string;
  /** MIME type if available */
  mimeType?: string;
}

export interface FileContextError {
  fileName: string;
  error: string;
}

export interface UseFileContextOptions {
  /** Maximum file size in bytes */
  maxFileSize?: number;
  /** Maximum total context size in bytes */
  maxTotalSize?: number;
  /** Maximum number of files */
  maxFiles?: number;
  /** Callback when files are added */
  onFilesAdded?: (files: FileContext[]) => void;
  /** Callback when error occurs */
  onError?: (error: FileContextError) => void;
}

export interface UseFileContextReturn {
  /** Current attached files */
  files: FileContext[];
  /** Total size of all attached files */
  totalSize: number;
  /** Whether files are being processed */
  isProcessing: boolean;
  /** Add files from a FileList or File array (for drag-and-drop) */
  addFiles: (files: FileList | File[]) => Promise<void>;
  /** Add a file with pre-extracted content (for FIAS file picker) */
  addFileWithContent: (file: { name: string; content: string; mimeType?: string }) => boolean;
  /** Remove a file by ID */
  removeFile: (id: string) => void;
  /** Clear all files */
  clearFiles: () => void;
  /** Format file context for AI query */
  formatContext: () => string;
  /** Check if a file type is supported */
  isFileSupported: (file: File) => boolean;
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '';
}

/**
 * Check if a file is a supported file
 */
function isSupportedFile(file: File): boolean {
  // Check by MIME type for text files
  if (file.type.startsWith('text/')) return true;
  if (file.type === 'application/json') return true;
  if (file.type === 'application/xml') return true;
  if (file.type === 'application/javascript') return true;
  if (file.type === 'application/typescript') return true;
  if (file.type === 'application/x-yaml') return true;

  // Check by MIME type for document files
  if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    return true;
  if (file.type === 'application/pdf') return true;

  // Check by extension
  const ext = getFileExtension(file.name);
  return SUPPORTED_EXTENSIONS.has(ext);
}

/**
 * Check if file is a DOCX file
 */
function isDocxFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  return (
    ext === '.docx' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

/**
 * Check if file is a PDF file
 */
function isPdfFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  return ext === '.pdf' || file.type === 'application/pdf';
}

/**
 * Read file content as text (for plain text files)
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsText(file);
  });
}

/**
 * Read file content as ArrayBuffer (for binary files)
 */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Extract text from DOCX file using mammoth (dynamically imported)
 */
async function extractTextFromDocx(file: File): Promise<string> {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const mammoth = await import('mammoth');
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.trim();
}

/**
 * Extract text from PDF file using pdf.js (dynamically imported)
 */
async function extractTextFromPdf(file: File): Promise<string> {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  // The shared loader owns worker setup (and the host's choice of pdf.js
  // build); a second inline `GlobalWorkerOptions` site is the duplication
  // docs/pdf-core.md exists to prevent.
  const pdfjsLib = await loadPdfJs();

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const textParts: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map((item) => ('str' in item ? item.str : '')).join(' ');
    textParts.push(pageText);
  }

  return textParts.join('\n\n').trim();
}

/**
 * Read file and extract text content based on file type
 */
async function extractFileContent(file: File): Promise<string> {
  if (isDocxFile(file)) {
    return extractTextFromDocx(file);
  }

  if (isPdfFile(file)) {
    return extractTextFromPdf(file);
  }

  // Default: read as text
  return readFileAsText(file);
}

/**
 * Generate unique ID for file
 */
function generateId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Hook for managing file context attachments
 */
export function useFileContext(options: UseFileContextOptions = {}): UseFileContextReturn {
  const {
    maxFileSize = MAX_FILE_SIZE,
    maxTotalSize = MAX_TOTAL_CONTEXT_SIZE,
    maxFiles = MAX_FILES,
    onFilesAdded,
    onError,
  } = options;

  const [files, setFiles] = useState<FileContext[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);

  const isFileSupported = useCallback((file: File): boolean => {
    return isSupportedFile(file);
  }, []);

  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const fileArray = Array.from(fileList);
      if (fileArray.length === 0) return;

      setIsProcessing(true);
      const newFiles: FileContext[] = [];
      let currentTotalSize = totalSize;

      try {
        for (const file of fileArray) {
          // Check file count limit
          if (files.length + newFiles.length >= maxFiles) {
            onError?.({
              fileName: file.name,
              error: `Maximum ${maxFiles} files allowed`,
            });
            break;
          }

          // Check if file type is supported
          if (!isSupportedFile(file)) {
            onError?.({
              fileName: file.name,
              error: 'Unsupported file type. Supported: text files, .docx, .pdf',
            });
            continue;
          }

          // Check individual file size
          if (file.size > maxFileSize) {
            onError?.({
              fileName: file.name,
              error: `File exceeds maximum size of ${formatBytes(maxFileSize)}`,
            });
            continue;
          }

          // Check for duplicate file names
          const isDuplicate =
            files.some((f) => f.name === file.name) || newFiles.some((f) => f.name === file.name);
          if (isDuplicate) {
            onError?.({
              fileName: file.name,
              error: 'File with this name is already attached',
            });
            continue;
          }

          try {
            const content = await extractFileContent(file);

            // For document files, check the extracted text size against the limit
            const contentSize = new Blob([content]).size;

            // Check total size limit (based on extracted text size)
            if (currentTotalSize + contentSize > maxTotalSize) {
              onError?.({
                fileName: file.name,
                error: `Total context would exceed ${formatBytes(maxTotalSize)} limit`,
              });
              continue;
            }

            if (content.length === 0) {
              onError?.({
                fileName: file.name,
                error: 'No text content could be extracted from this file',
              });
              continue;
            }

            const fileContext: FileContext = {
              id: generateId(),
              name: file.name,
              size: contentSize, // Use extracted content size
              content,
              mimeType: file.type || undefined,
            };
            newFiles.push(fileContext);
            currentTotalSize += contentSize;
          } catch (err) {
            onError?.({
              fileName: file.name,
              error: err instanceof Error ? err.message : 'Failed to read file',
            });
          }
        }

        if (newFiles.length > 0) {
          setFiles((prev) => [...prev, ...newFiles]);
          onFilesAdded?.(newFiles);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [files, totalSize, maxFiles, maxFileSize, maxTotalSize, onFilesAdded, onError],
  );

  const addFileWithContent = useCallback(
    (file: { name: string; content: string; mimeType?: string }): boolean => {
      const contentSize = new Blob([file.content]).size;

      // Check file count limit
      if (files.length >= maxFiles) {
        onError?.({
          fileName: file.name,
          error: `Maximum ${maxFiles} files allowed`,
        });
        return false;
      }

      // Check total size limit
      if (totalSize + contentSize > maxTotalSize) {
        onError?.({
          fileName: file.name,
          error: `Total context would exceed ${formatBytes(maxTotalSize)} limit`,
        });
        return false;
      }

      // Check for duplicate file names
      if (files.some((f) => f.name === file.name)) {
        onError?.({
          fileName: file.name,
          error: 'File with this name is already attached',
        });
        return false;
      }

      const fileContext: FileContext = {
        id: generateId(),
        name: file.name,
        size: contentSize,
        content: file.content,
        mimeType: file.mimeType,
      };

      setFiles((prev) => [...prev, fileContext]);
      onFilesAdded?.([fileContext]);
      return true;
    },
    [files, totalSize, maxFiles, maxTotalSize, onFilesAdded, onError],
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const formatContext = useCallback((): string => {
    if (files.length === 0) return '';

    const parts = files.map((file) => {
      return `--- File: ${file.name} ---\n${file.content}\n--- End of ${file.name} ---`;
    });

    return `The following files are attached as context:\n\n${parts.join('\n\n')}\n\n`;
  }, [files]);

  return {
    files,
    totalSize,
    isProcessing,
    addFiles,
    addFileWithContent,
    removeFile,
    clearFiles,
    formatContext,
    isFileSupported,
  };
}

export default useFileContext;
